import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { collection, doc, getDoc, getDocs, orderBy, query, serverTimestamp, writeBatch } from 'firebase/firestore';
import { AlertTriangle, BookOpen, CheckCircle2, Loader2, RefreshCw, Save, Search, Users } from 'lucide-react';
import { db } from '../../firebase/config';
import { getConfiguredSubjects, SEMESTER_MONTHS } from '../../utils/semesterConfig';

const currentYear = new Date().getFullYear();

const normalizeText = (value) => String(value || '').trim().toLowerCase();

const numberOrZero = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const cleanCountInput = (value, maxValue = null) => {
  if (value === '') return '';
  const parsed = Number.parseInt(String(value).replace(/\D/g, ''), 10);
  if (!Number.isFinite(parsed) || parsed < 0) return '';
  const hasMax = maxValue !== null && maxValue !== undefined && maxValue !== '';
  const max = Number(maxValue);
  if (hasMax && Number.isFinite(max) && max >= 0 && parsed > max) return String(max);
  return String(parsed);
};

const percentage = (attended, conducted) => (
  conducted > 0 ? Number(((attended / conducted) * 100).toFixed(2)) : 0
);

const mergeCountValues = (...values) => {
  const presentValues = values.filter((value) => value !== undefined && value !== null && value !== '');
  if (presentValues.length === 0) return '';
  return String(presentValues.reduce((sum, value) => sum + numberOrZero(value), 0));
};

const monthKey = (month) => String(month || '').replace(/[^a-z0-9]/gi, '_').toLowerCase();

const courseAttendanceDocId = ({ rollNumber, semester, subjectId, month, year }) => (
  `${rollNumber}_semester${semester}_${subjectId}_${year}_${monthKey(month)}`.replace(/[^a-z0-9_-]/gi, '_')
);

const aggregateEntryId = ({ semester, month, year }) => (
  `course_semester${semester}_${year}_${monthKey(month)}`
);

const studentName = (student) => (
  `${student.firstName || ''} ${student.surname || ''}`.trim() ||
  student.name ||
  student.rollNumber ||
  ''
);

const facultyMatchesSubject = (subject, identities) => {
  const email = normalizeText(subject.courseInchargeEmail);
  const name = normalizeText(subject.courseInchargeName);
  return Boolean((email && identities.has(email)) || (name && identities.has(name)));
};

const subjectWithCourseDivision = (subject, teachingDivision) => ({
  ...subject,
  teachingDivision: teachingDivision || subject.teachingDivision || ''
});

const getCourseRecord = (records, period, rollNumber, subjectId) => (
  records[courseAttendanceDocId({ ...period, rollNumber, subjectId })] || null
);

const makeRow = ({ student, record }) => ({
  rollNumber: student.rollNumber,
  name: studentName(student),
  division: student.division || '',
  batch: student.batch || '',
  lectureAttended: record?.theoryTotal?.attended ?? '',
  practicalAttended: mergeCountValues(record?.practicalTotal?.attended, record?.tutorialTotal?.attended),
  dirty: false
});

const getRecordNumbers = (record) => {
  const theoryConducted = numberOrZero(record?.theoryTotal?.conducted ?? record?.lectureConducted);
  const theoryAttended = numberOrZero(record?.theoryTotal?.attended ?? record?.lectureAttended);
  const practicalConducted =
    numberOrZero(record?.practicalTotal?.conducted ?? record?.practicalConducted) +
    numberOrZero(record?.tutorialTotal?.conducted ?? record?.tutorialConducted);
  const practicalAttended =
    numberOrZero(record?.practicalTotal?.attended ?? record?.practicalAttended) +
    numberOrZero(record?.tutorialTotal?.attended ?? record?.tutorialAttended);

  return {
    theoryConducted,
    theoryAttended,
    practicalConducted,
    practicalAttended
  };
};

const buildCourseAttendancePayload = ({
  row,
  subject,
  period,
  lectureConducted,
  practicalConducted,
  userEmail,
  userName
}) => {
  const theoryConducted = numberOrZero(lectureConducted);
  const theoryAttended = numberOrZero(row.lectureAttended);
  const practicalTotal = numberOrZero(practicalConducted);
  const practicalAttended = numberOrZero(row.practicalAttended);
  const totalLectures = theoryConducted + practicalTotal;
  const attendedLectures = theoryAttended + practicalAttended;

  return {
    rollNumber: row.rollNumber,
    studentName: row.name,
    division: row.division,
    batch: row.batch,
    semester: Number(period.semester),
    month: period.month,
    year: Number(period.year),
    subjectId: subject.id,
    subjectCode: subject.code || '',
    subjectName: subject.name || '',
    teachingDivision: subject.teachingDivision || row.division || '',
    courseInchargeEmail: subject.courseInchargeEmail || userEmail || '',
    courseInchargeName: subject.courseInchargeName || userName || '',
    theoryTotal: {
      conducted: theoryConducted,
      attended: theoryAttended
    },
    practicalTotal: {
      conducted: practicalTotal,
      attended: practicalAttended
    },
    tutorialTotal: {
      conducted: 0,
      attended: 0
    },
    totalLectures,
    attendedLectures,
    theoryPercentage: percentage(theoryAttended, theoryConducted),
    practicalPercentage: percentage(practicalAttended, practicalTotal),
    tutorialPercentage: 0,
    overallPercentage: percentage(attendedLectures, totalLectures),
    updatedAt: serverTimestamp(),
    updatedBy: userName || userEmail || 'Course Incharge'
  };
};

const buildCourseRowsForReport = (records) => records.flatMap((record) => {
  const subjectLabel = `${record.subjectCode ? `${record.subjectCode} - ` : ''}${record.subjectName || 'Course'}`;
  const theory = getRecordNumbers(record);
  const rows = [];

  if (theory.theoryConducted > 0 || theory.theoryAttended > 0) {
    rows.push({
      code: record.subjectCode || record.subjectId || '',
      name: `${subjectLabel} Theory`,
      type: 'theory',
      subjectId: record.subjectId || '',
      conducted: theory.theoryConducted,
      attended: theory.theoryAttended
    });
  }

  if (theory.practicalConducted > 0 || theory.practicalAttended > 0) {
    rows.push({
      code: record.subjectCode || record.subjectId || '',
      name: `${subjectLabel} Tutorials/Practicals`,
      type: 'practical',
      subjectId: record.subjectId || '',
      conducted: theory.practicalConducted,
      attended: theory.practicalAttended
    });
  }

  return rows;
});

const buildAggregateAttendanceRecord = ({
  row,
  existingAttendance,
  courseAttendanceRecords,
  period,
  userName
}) => {
  const records = Object.values(courseAttendanceRecords).filter((record) => (
    record.rollNumber === row.rollNumber &&
    Number(record.semester) === Number(period.semester) &&
    record.month === period.month &&
    String(record.year || period.year) === String(period.year)
  ));

  const totals = records.reduce((acc, record) => {
    const numbers = getRecordNumbers(record);
    acc.theoryConducted += numbers.theoryConducted;
    acc.theoryAttended += numbers.theoryAttended;
    acc.practicalConducted += numbers.practicalConducted;
    acc.practicalAttended += numbers.practicalAttended;
    return acc;
  }, {
    theoryConducted: 0,
    theoryAttended: 0,
    practicalConducted: 0,
    practicalAttended: 0
  });

  const totalLectures = totals.theoryConducted + totals.practicalConducted;
  const attendedLectures = totals.theoryAttended + totals.practicalAttended;
  const courses = buildCourseRowsForReport(records);
  const entryId = aggregateEntryId(period);
  const entry = {
    id: entryId,
    source: 'courseAttendance',
    semester: Number(period.semester),
    month: period.month,
    year: Number(period.year),
    theoryTotal: {
      conducted: totals.theoryConducted,
      attended: totals.theoryAttended
    },
    practicalTotal: {
      conducted: totals.practicalConducted,
      attended: totals.practicalAttended
    },
    tutorialTotal: {
      conducted: 0,
      attended: 0
    },
    totalLectures,
    attendedLectures,
    theoryPercentage: percentage(totals.theoryAttended, totals.theoryConducted),
    practicalPercentage: percentage(totals.practicalAttended, totals.practicalConducted),
    tutorialPercentage: 0,
    overallPercentage: percentage(attendedLectures, totalLectures),
    courses,
    updatedAt: serverTimestamp(),
    updatedBy: userName || 'Course Incharge'
  };
  const previousEntries = Array.isArray(existingAttendance?.entries) ? existingAttendance.entries : [];

  return {
    ...(existingAttendance || {}),
    rollNumber: row.rollNumber,
    studentName: row.name,
    division: row.division,
    batch: row.batch,
    semester: Number(period.semester),
    month: period.month,
    year: Number(period.year),
    totalLectures,
    attendedLectures,
    percentage: entry.overallPercentage,
    overallPercentage: entry.overallPercentage,
    theoryPercentage: entry.theoryPercentage,
    practicalPercentage: entry.practicalPercentage,
    tutorialPercentage: entry.tutorialPercentage,
    theoryTotal: entry.theoryTotal,
    practicalTotal: entry.practicalTotal,
    tutorialTotal: entry.tutorialTotal,
    courses,
    entries: [
      entry,
      ...previousEntries.filter((item) => item.id !== entryId)
    ].slice(0, 36),
    updatedAt: serverTimestamp(),
    updatedBy: userName || 'Course Incharge'
  };
};

const CourseAttendanceEntryWidget = ({
  userEmail,
  userName,
  userId,
  username,
  assignedDivision,
  courseAssignment
}) => {
  const [semester, setSemester] = useState(String(courseAssignment?.semester || '1'));
  const [month, setMonth] = useState(SEMESTER_MONTHS[Number(courseAssignment?.semester || 1)]?.[0] || 'August');
  const [year, setYear] = useState(currentYear);
  const [assignedSubjects, setAssignedSubjects] = useState([]);
  const [selectedSubjectId, setSelectedSubjectId] = useState('');
  const [students, setStudents] = useState([]);
  const [courseAttendanceMap, setCourseAttendanceMap] = useState({});
  const [attendanceMap, setAttendanceMap] = useState({});
  const [rows, setRows] = useState([]);
  const [lectureConducted, setLectureConducted] = useState('');
  const [practicalConducted, setPracticalConducted] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const attendanceInputRefs = useRef({});

  const identities = useMemo(() => new Set([
    userEmail,
    userName,
    userId,
    username
  ].map(normalizeText).filter(Boolean)), [userEmail, userId, userName, username]);
  const lockedToCourse = Boolean(courseAssignment?.subjectId);
  const activeSemester = String(courseAssignment?.semester || semester);
  const courseAssignmentSubjectId = courseAssignment?.subjectId || '';
  const courseAssignmentDivision = courseAssignment?.teachingDivision || courseAssignment?.division || '';
  const months = SEMESTER_MONTHS[Number(activeSemester)] || SEMESTER_MONTHS[1];

  const selectedSubject = useMemo(() => (
    assignedSubjects.find((subject) => subject.id === selectedSubjectId) || assignedSubjects[0] || null
  ), [assignedSubjects, selectedSubjectId]);

  const period = useMemo(() => ({
    semester: activeSemester,
    month,
    year
  }), [activeSemester, month, year]);

  useEffect(() => {
    if (!lockedToCourse) return;
    setSemester(String(courseAssignment.semester || '1'));
    setSelectedSubjectId(courseAssignment.subjectId || '');
  }, [courseAssignment?.semester, courseAssignment?.subjectId, lockedToCourse]);

  useEffect(() => {
    if (months.includes(month)) return;
    setMonth(months[0] || 'August');
  }, [month, months]);

  const rebuildRows = useCallback((studentList, attendanceRecords, courseRecords, subject) => {
    if (!subject) {
      setRows([]);
      return;
    }

    const divisionScope = subject.teachingDivision || assignedDivision || '';
    const scopedStudents = divisionScope
      ? studentList.filter((student) => student.division === divisionScope)
      : studentList;
    const nextRows = scopedStudents.map((student) => makeRow({
      student,
      record: getCourseRecord(courseRecords, period, student.rollNumber, subject.id)
    }));
    const firstRecord = nextRows
      .map((row) => getCourseRecord(courseRecords, period, row.rollNumber, subject.id))
      .find(Boolean);

    setLectureConducted(firstRecord?.theoryTotal?.conducted ?? '');
    setPracticalConducted(mergeCountValues(
      firstRecord?.practicalTotal?.conducted,
      firstRecord?.tutorialTotal?.conducted
    ));
    setAttendanceMap(attendanceRecords);
    setRows(nextRows);
  }, [assignedDivision, period]);

  useEffect(() => {
    if (loading || !selectedSubject || students.length === 0) return;
    rebuildRows(students, attendanceMap, courseAttendanceMap, selectedSubject);
  }, [attendanceMap, courseAttendanceMap, loading, rebuildRows, selectedSubject, students]);

  const loadAttendanceSheet = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setMessage('');

      const [subjectsDoc, studentsSnap, courseAttendanceSnap, attendanceSnap] = await Promise.all([
        getDoc(doc(db, 'semesterSubjects', `semester${activeSemester}`)),
        getDocs(query(collection(db, 'students'), orderBy('rollNumber', 'asc'))),
        getDocs(collection(db, 'courseAttendance')),
        getDocs(collection(db, 'attendance'))
      ]);

      const configuredSubjects = getConfiguredSubjects(
        subjectsDoc.exists() ? subjectsDoc.data() : null,
        Number(activeSemester)
      );
      const nextAssignedSubjects = lockedToCourse
        ? configuredSubjects
            .filter((subject) => subject.id === courseAssignmentSubjectId)
            .map((subject) => subjectWithCourseDivision(subject, courseAssignmentDivision))
        : configuredSubjects.filter((subject) => facultyMatchesSubject(subject, identities));
      const preferredSubjectId = lockedToCourse ? courseAssignmentSubjectId : selectedSubjectId;
      const nextSubject = nextAssignedSubjects.find((subject) => subject.id === preferredSubjectId) ||
        nextAssignedSubjects[0] ||
        null;
      const nextStudents = studentsSnap.docs.map((studentDoc) => studentDoc.data());
      const nextCourseAttendanceMap = {};
      const nextAttendanceMap = {};

      courseAttendanceSnap.docs.forEach((attendanceDoc) => {
        nextCourseAttendanceMap[attendanceDoc.id] = attendanceDoc.data();
      });
      attendanceSnap.docs.forEach((attendanceDoc) => {
        nextAttendanceMap[attendanceDoc.id] = attendanceDoc.data();
      });

      setAssignedSubjects(nextAssignedSubjects);
      setSelectedSubjectId(nextSubject?.id || '');
      setStudents(nextStudents);
      setCourseAttendanceMap(nextCourseAttendanceMap);
      rebuildRows(nextStudents, nextAttendanceMap, nextCourseAttendanceMap, nextSubject);
    } catch (loadError) {
      console.error('Error loading course attendance sheet:', loadError);
      setError('Could not load course attendance sheet.');
    } finally {
      setLoading(false);
    }
  }, [activeSemester, courseAssignmentDivision, courseAssignmentSubjectId, identities, lockedToCourse, rebuildRows, selectedSubjectId]);

  useEffect(() => {
    loadAttendanceSheet();
  }, [loadAttendanceSheet]);

  const changeSubject = (subjectId) => {
    if (lockedToCourse) return;
    const subject = assignedSubjects.find((item) => item.id === subjectId) || null;
    setSelectedSubjectId(subjectId);
    setMessage('');
    setError('');
    rebuildRows(students, attendanceMap, courseAttendanceMap, subject);
  };

  const changeMonth = (nextMonth) => {
    setMonth(nextMonth);
    setMessage('');
    setError('');
  };

  const updateConducted = (field, value) => {
    const nextValue = cleanCountInput(value);
    const isLecture = field === 'lecture';
    const isPractical = field === 'practical';

    if (isLecture) {
      setLectureConducted(nextValue);
    } else if (isPractical) {
      setPracticalConducted(nextValue);
    }

    setRows((currentRows) => currentRows.map((row) => ({
      ...row,
      lectureAttended: isLecture ? cleanCountInput(row.lectureAttended, nextValue) : row.lectureAttended,
      practicalAttended: isPractical ? cleanCountInput(row.practicalAttended, nextValue) : row.practicalAttended,
      dirty: true
    })));
  };

  const updateRow = (rollNumber, field, value) => {
    const maxByField = {
      lectureAttended: lectureConducted,
      practicalAttended: practicalConducted
    };
    const max = maxByField[field];
    const nextValue = cleanCountInput(value, max);

    setRows((currentRows) => currentRows.map((row) => (
      row.rollNumber === rollNumber
        ? { ...row, [field]: nextValue, dirty: true }
        : row
    )));
  };

  const filteredRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return rows;

    return rows.filter((row) => (
      row.rollNumber.toLowerCase().includes(needle) ||
      row.name.toLowerCase().includes(needle) ||
      row.division.toLowerCase().includes(needle) ||
      row.batch.toLowerCase().includes(needle)
    ));
  }, [rows, search]);

  const handleAttendanceKeyDown = (event, rowIndex, field) => {
    if (event.key !== 'Enter') return;

    event.preventDefault();
    const nextIndex = event.shiftKey ? rowIndex - 1 : rowIndex + 1;
    const nextRow = filteredRows[nextIndex];

    if (!nextRow) {
      event.currentTarget.blur();
      return;
    }

    const nextInput = attendanceInputRefs.current[`${nextRow.rollNumber}:${field}`];
    if (nextInput) {
      nextInput.focus();
      nextInput.select();
    }
  };

  const dirtyRows = rows.filter((row) => row.dirty);
  const invalidRows = rows.filter((row) => (
    numberOrZero(row.lectureAttended) > numberOrZero(lectureConducted) ||
    numberOrZero(row.practicalAttended) > numberOrZero(practicalConducted)
  ));
  const canSave = Boolean(selectedSubject) && dirtyRows.length > 0 && !saving && invalidRows.length === 0;

  const saveRows = async () => {
    if (!canSave) return;

    try {
      setSaving(true);
      setError('');
      setMessage('');

      let batch = writeBatch(db);
      let writeCount = 0;
      const nextCourseAttendanceMap = { ...courseAttendanceMap };
      const nextAttendanceMap = { ...attendanceMap };

      const commitIfNeeded = async (force = false) => {
        if (!writeCount) return;
        if (!force && writeCount < 450) return;
        await batch.commit();
        batch = writeBatch(db);
        writeCount = 0;
      };

      for (const row of dirtyRows) {
        const courseId = courseAttendanceDocId({
          ...period,
          rollNumber: row.rollNumber,
          subjectId: selectedSubject.id
        });
        const coursePayload = buildCourseAttendancePayload({
          row,
          subject: selectedSubject,
          period,
          lectureConducted,
          practicalConducted,
          userEmail,
          userName
        });

        nextCourseAttendanceMap[courseId] = coursePayload;
        batch.set(doc(db, 'courseAttendance', courseId), coursePayload, { merge: true });
        writeCount += 1;

        const aggregatePayload = buildAggregateAttendanceRecord({
          row,
          existingAttendance: nextAttendanceMap[row.rollNumber],
          courseAttendanceRecords: nextCourseAttendanceMap,
          period,
          userName
        });
        nextAttendanceMap[row.rollNumber] = aggregatePayload;
        batch.set(doc(db, 'attendance', row.rollNumber), aggregatePayload, { merge: true });
        writeCount += 1;

        await commitIfNeeded();
      }

      await commitIfNeeded(true);

      setCourseAttendanceMap(nextCourseAttendanceMap);
      setAttendanceMap(nextAttendanceMap);
      setRows((currentRows) => currentRows.map((row) => ({ ...row, dirty: false })));
      setMessage(`Saved ${period.month} attendance for ${dirtyRows.length} student${dirtyRows.length === 1 ? '' : 's'}.`);
    } catch (saveError) {
      console.error('Error saving course attendance:', saveError);
      setError('Could not save course attendance. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const totals = useMemo(() => {
    const theoryConducted = rows.length * numberOrZero(lectureConducted);
    const practicalTotal = rows.length * numberOrZero(practicalConducted);
    const theoryAttended = rows.reduce((sum, row) => sum + numberOrZero(row.lectureAttended), 0);
    const practicalAttended = rows.reduce((sum, row) => sum + numberOrZero(row.practicalAttended), 0);
    return {
      conducted: theoryConducted + practicalTotal,
      attended: theoryAttended + practicalAttended,
      percentage: percentage(theoryAttended + practicalAttended, theoryConducted + practicalTotal)
    };
  }, [lectureConducted, practicalConducted, rows]);

  if (loading) {
    return (
      <div className="flex min-h-[260px] items-center justify-center text-gray-500">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        Loading course attendance sheet
      </div>
    );
  }

  if (!assignedSubjects.length) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-5 text-sm text-amber-800">
        <div className="flex items-center font-semibold">
          <AlertTriangle className="mr-2 h-4 w-4" />
          No subject is assigned to this faculty account.
        </div>
        <p className="mt-2">
          Ask the HOD to assign this faculty as Course Incharge in Semester Subject Management.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-12">
        <label className="lg:col-span-2">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Semester</span>
          <select
            value={activeSemester}
            disabled={lockedToCourse}
            onChange={(event) => {
              setSemester(event.target.value);
              setSelectedSubjectId('');
            }}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-50 disabled:text-gray-600"
          >
            <option value="1">Semester 1</option>
            <option value="2">Semester 2</option>
          </select>
        </label>

        <label className="lg:col-span-4">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Subject</span>
          <select
            value={selectedSubject?.id || ''}
            disabled={lockedToCourse}
            onChange={(event) => changeSubject(event.target.value)}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-50 disabled:text-gray-600"
          >
            {assignedSubjects.map((subject) => (
              <option key={subject.id} value={subject.id}>
                {subject.code ? `${subject.code} - ` : ''}{subject.name}{subject.teachingDivision ? ` (Division ${subject.teachingDivision})` : ''}
              </option>
            ))}
          </select>
        </label>

        <label className="lg:col-span-2">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Academic Year</span>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength="4"
            value={year}
            onChange={(event) => setYear(cleanCountInput(event.target.value).slice(0, 4))}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </label>

        <div className="lg:col-span-2">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Scope</span>
          <div className="flex items-center rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-700">
            <Users className="mr-2 h-4 w-4 text-gray-500" />
            {selectedSubject?.teachingDivision
              ? `Division ${selectedSubject.teachingDivision}`
              : assignedDivision
                ? `Division ${assignedDivision}`
                : 'All students'}
          </div>
        </div>

        <div className="flex items-end gap-2 lg:col-span-2">
          <button
            type="button"
            onClick={loadAttendanceSheet}
            className="inline-flex flex-1 items-center justify-center rounded border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Load
          </button>
          <button
            type="button"
            onClick={saveRows}
            disabled={!canSave}
            className="inline-flex flex-1 items-center justify-center rounded bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Save
          </button>
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto border-b border-gray-200 pb-2">
        {months.map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => changeMonth(item)}
            className={`whitespace-nowrap rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
              item === month
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50'
            }`}
          >
            {item}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-12">
        <label className="lg:col-span-3">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Lectures Conducted</span>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={lectureConducted}
            onChange={(event) => updateConducted('lecture', event.target.value)}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </label>

        <label className="lg:col-span-3">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Tutorials/Practicals Conducted</span>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={practicalConducted}
            onChange={(event) => updateConducted('practical', event.target.value)}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </label>

        <label className="lg:col-span-3">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Search</span>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Roll, name, division, batch"
              className="w-full rounded border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
        </label>

        <div className="lg:col-span-3">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Sheet Summary</span>
          <div className="flex items-center rounded border border-indigo-100 bg-indigo-50 px-3 py-2 text-sm text-indigo-900">
            <BookOpen className="mr-2 h-4 w-4" />
            <span className="font-semibold">{totals.percentage.toFixed(2)}%</span>
            <span className="ml-1">overall ({totals.attended}/{totals.conducted})</span>
          </div>
        </div>
      </div>

      {invalidRows.length > 0 && (
        <div className="flex items-center rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertTriangle className="mr-2 h-4 w-4" />
          {invalidRows.length} row{invalidRows.length === 1 ? '' : 's'} have attended counts greater than conducted counts.
        </div>
      )}

      {error && (
        <div className="flex items-center rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertTriangle className="mr-2 h-4 w-4" />
          {error}
        </div>
      )}

      {message && (
        <div className="flex items-center rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
          <CheckCircle2 className="mr-2 h-4 w-4" />
          {message}
        </div>
      )}

      <div className="overflow-x-auto rounded border border-gray-200">
        <table className="min-w-[1120px] w-full border-collapse text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="sticky left-0 z-10 border-b border-r border-gray-200 bg-gray-50 px-3 py-2 text-left">Roll</th>
              <th className="border-b border-r border-gray-200 px-3 py-2 text-left">Student</th>
              <th className="border-b border-r border-gray-200 px-3 py-2 text-left">Division</th>
              <th className="border-b border-r border-gray-200 px-3 py-2 text-left">Batch</th>
              <th className="border-b border-r border-gray-200 px-3 py-2 text-center">Lectures Attended</th>
              <th className="border-b border-r border-gray-200 px-3 py-2 text-center">Tutorials/Practicals Attended</th>
              <th className="border-b border-r border-gray-200 px-3 py-2 text-center">Lecture %</th>
              <th className="border-b border-r border-gray-200 px-3 py-2 text-center">T/P %</th>
              <th className="border-b border-gray-200 px-3 py-2 text-center">Overall %</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 ? (
              <tr>
                <td colSpan="9" className="px-3 py-8 text-center text-gray-500">
                  No students found.
                </td>
              </tr>
            ) : (
              filteredRows.map((row, rowIndex) => {
                const lectureTotal = numberOrZero(lectureConducted);
                const practicalTotal = numberOrZero(practicalConducted);
                const lectureAttended = numberOrZero(row.lectureAttended);
                const practicalAttended = numberOrZero(row.practicalAttended);
                const totalConducted = lectureTotal + practicalTotal;
                const totalAttended = lectureAttended + practicalAttended;
                const theoryPercent = percentage(lectureAttended, lectureTotal);
                const practicalPercent = percentage(practicalAttended, practicalTotal);
                const overallPercent = percentage(totalAttended, totalConducted);

                return (
                  <tr key={row.rollNumber} className={row.dirty ? 'bg-indigo-50/50' : 'bg-white'}>
                    <td className="sticky left-0 z-10 border-r border-t border-gray-200 bg-inherit px-3 py-2 font-semibold text-gray-900">
                      {row.rollNumber}
                    </td>
                    <td className="border-r border-t border-gray-200 px-3 py-2 text-gray-800">{row.name}</td>
                    <td className="border-r border-t border-gray-200 px-3 py-2 text-gray-600">{row.division || '-'}</td>
                    <td className="border-r border-t border-gray-200 px-3 py-2 text-gray-600">{row.batch || '-'}</td>
                    <td className="border-r border-t border-gray-200 px-2 py-1">
                      <input
                        ref={(input) => {
                          attendanceInputRefs.current[`${row.rollNumber}:lectureAttended`] = input;
                        }}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={row.lectureAttended}
                        onChange={(event) => updateRow(row.rollNumber, 'lectureAttended', event.target.value)}
                        onKeyDown={(event) => handleAttendanceKeyDown(event, rowIndex, 'lectureAttended')}
                        className="w-full rounded border border-transparent bg-transparent px-2 py-1 text-center focus:border-indigo-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      />
                    </td>
                    <td className="border-r border-t border-gray-200 px-2 py-1">
                      <input
                        ref={(input) => {
                          attendanceInputRefs.current[`${row.rollNumber}:practicalAttended`] = input;
                        }}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={row.practicalAttended}
                        onChange={(event) => updateRow(row.rollNumber, 'practicalAttended', event.target.value)}
                        onKeyDown={(event) => handleAttendanceKeyDown(event, rowIndex, 'practicalAttended')}
                        className="w-full rounded border border-transparent bg-transparent px-2 py-1 text-center focus:border-indigo-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      />
                    </td>
                    <td className={`border-r border-t border-gray-200 px-3 py-2 text-center font-semibold ${theoryPercent < 75 && lectureTotal > 0 ? 'text-red-600' : 'text-green-700'}`}>
                      {lectureTotal ? theoryPercent.toFixed(2) : '-'}
                    </td>
                    <td className={`border-r border-t border-gray-200 px-3 py-2 text-center font-semibold ${practicalPercent < 75 && practicalTotal > 0 ? 'text-red-600' : 'text-green-700'}`}>
                      {practicalTotal ? practicalPercent.toFixed(2) : '-'}
                    </td>
                    <td className={`border-t border-gray-200 px-3 py-2 text-center font-semibold ${overallPercent < 75 && totalConducted > 0 ? 'text-red-600' : 'text-green-700'}`}>
                      {totalConducted ? overallPercent.toFixed(2) : '-'}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CourseAttendanceEntryWidget;
